import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { getUsers, updateUser, deleteUser, register, getRestaurants, adminUpdateUserPassword } from '../services/api';
import AppShell from '../components/layout/AppShell';
import './UserManagement.css';

const UserManagement = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verifyDialogUser, setVerifyDialogUser] = useState(null);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [resultDialogMessage, setResultDialogMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogUser, setDeleteDialogUser] = useState(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [resetPasswordSubmitting, setResetPasswordSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    role: 'manager',
    restaurantIds: [],
    status: 'active',
    verified: false
  });

  useEffect(() => {
    if (isAdmin()) {
      fetchData();
    }
  }, [isAdmin]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, restaurantsRes] = await Promise.all([
        getUsers(),
        getRestaurants()
      ]);
      setUsers(usersRes.data);
      setRestaurants(restaurantsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Map: restaurantId -> userId (each restaurant can have only one assigned user)
  const assignedRestaurantToUserId = useMemo(() => {
    const map = {};
    users.forEach(user => {
      if (user.restaurant_names && user.restaurant_names.length > 0) {
        const restaurantName = user.restaurant_names[0];
        const restaurant = restaurants.find(r => r.name === restaurantName);
        if (restaurant) map[restaurant.id] = user.id;
      }
    });
    return map;
  }, [users, restaurants]);

  // For "Assign to Restaurant": when creating, only unassigned; when editing, unassigned or current user's restaurant
  const availableRestaurantsForAssignment = useMemo(() => {
    return restaurants.filter(r => {
      const assignedToUserId = assignedRestaurantToUserId[r.id];
      if (!assignedToUserId) return true; // unassigned
      if (editingUser && assignedToUserId === editingUser.id) return true; // assigned to user we're editing
      return false; // assigned to another user
    });
  }, [restaurants, assignedRestaurantToUserId, editingUser]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    // Single restaurant assignment: one restaurant only or unassigned
    if (name === 'restaurantId') {
      setFormData(prev => ({
        ...prev,
        restaurantIds: value ? [parseInt(value, 10)] : []
      }));
      return;
    }
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: e.target.checked }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    // A user can be assigned to one restaurant only or unassigned
    const restaurantIds = Array.isArray(formData.restaurantIds)
      ? formData.restaurantIds.slice(0, 1)
      : [];
    try {
      await register({ ...formData, restaurantIds });
      setShowCreateForm(false);
      resetForm();
      await fetchData();
      setResultDialogMessage(t('userManagement.messages.created'));
      setResultDialogOpen(true);
    } catch (error) {
      setResultDialogMessage(error.response?.data?.error || 'Failed to create user');
      setResultDialogOpen(true);
    }
  };

  const handleEditUser = async (user) => {
    setEditingUser(user);
    // One restaurant per user: take first if any
    let restaurantIds = [];
    if (user.role === 'manager' && user.restaurant_names && user.restaurant_names.length > 0) {
      const ids = restaurants
        .filter(r => user.restaurant_names.includes(r.name))
        .map(r => r.id);
      restaurantIds = ids.length > 0 ? [ids[0]] : [];
    }
    setFormData({
      email: user.email,
      password: '',
      name: user.name,
      phone: user.phone || '',
      role: user.role,
      restaurantIds,
      status: user.status,
      verified: Boolean(user.verified)
    });
    setShowCreateForm(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    // A user can be assigned to one restaurant only or unassigned
    const restaurantIds = Array.isArray(formData.restaurantIds)
      ? formData.restaurantIds.slice(0, 1)
      : [];
    try {
      await updateUser(editingUser.id, {
        name: formData.name,
        phone: formData.phone,
        role: formData.role,
        status: formData.status,
        restaurantIds,
        verified: formData.verified
      });
      setEditingUser(null);
      setShowCreateForm(false);
      resetForm();
      await fetchData();
      setResultDialogMessage(t('userManagement.messages.updated'));
      setResultDialogOpen(true);
    } catch (error) {
      setResultDialogMessage(error.response?.data?.error || 'Failed to update user');
      setResultDialogOpen(true);
    }
  };

  const handleDeleteClick = (user) => {
    setDeleteDialogUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogConfirm = async () => {
    if (!deleteDialogUser) return;
    const userId = deleteDialogUser.id;
    setDeleteDialogOpen(false);
    setDeleteDialogUser(null);
    try {
      await deleteUser(userId);
      await fetchData();
      setResultDialogMessage(t('userManagement.messages.deleted'));
      setResultDialogOpen(true);
    } catch (error) {
      setResultDialogMessage(error.response?.data?.error || 'Failed to delete user');
      setResultDialogOpen(true);
    }
  };

  const handleDeleteDialogCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteDialogUser(null);
  };

  const handleToggleVerifyClick = (user) => {
    setVerifyDialogUser(user);
    setVerifyDialogOpen(true);
  };

  const handleVerifyDialogConfirm = async () => {
    if (!verifyDialogUser) return;
    const user = verifyDialogUser;
    setVerifyDialogOpen(false);
    setVerifyDialogUser(null);
    try {
      const newVerified = !Boolean(user.verified);
      await updateUser(user.id, { verified: newVerified });
      await fetchData();
      setResultDialogMessage(
        newVerified ? 'User verified. They can now create restaurants.' : 'User unverified.'
      );
      setResultDialogOpen(true);
    } catch (error) {
      setResultDialogMessage(error.response?.data?.error || 'Failed to update user');
      setResultDialogOpen(true);
    }
  };

  const handleVerifyDialogCancel = () => {
    setVerifyDialogOpen(false);
    setVerifyDialogUser(null);
  };

  const handleResetPasswordClick = (user) => {
    setResetPasswordUser(user);
    setResetPasswordValue('');
    setResetPasswordError('');
    setResetPasswordDialogOpen(true);
  };

  const handleResetPasswordCancel = () => {
    if (resetPasswordSubmitting) return;
    setResetPasswordDialogOpen(false);
    setResetPasswordUser(null);
    setResetPasswordValue('');
    setResetPasswordError('');
  };

  const handleResetPasswordConfirm = async (e) => {
    e.preventDefault();
    if (!resetPasswordUser) return;
    if (resetPasswordValue.length < 8) {
      setResetPasswordError(t('userManagement.resetPasswordDialog.tooShort'));
      return;
    }
    setResetPasswordSubmitting(true);
    try {
      await adminUpdateUserPassword(resetPasswordUser.id, resetPasswordValue);
      const userName = resetPasswordUser.name;
      setResetPasswordDialogOpen(false);
      setResetPasswordUser(null);
      setResetPasswordValue('');
      setResetPasswordError('');
      setResultDialogMessage(t('userManagement.messages.passwordReset', { name: userName }));
      setResultDialogOpen(true);
    } catch (error) {
      setResetPasswordError(error.response?.data?.error || t('userManagement.resetPasswordDialog.failed'));
    } finally {
      setResetPasswordSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      phone: '',
      role: 'manager',
      restaurantIds: [],
      status: 'active',
      verified: false
    });
    setEditingUser(null);
  };

  if (!isAdmin()) {
    return (
      <AppShell>
        <div className="error-container">
          <h2>{t('userManagement.accessDenied.title')}</h2>
          <p>{t('userManagement.accessDenied.message')}</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
    <div className="user-management-page fade-in">
      <div className="user-management-container">
        <div className="page-header">
          <div>
            <h1>{t('userManagement.title')}</h1>
            <p>{t('userManagement.subtitle')}</p>
          </div>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              resetForm();
              setShowCreateForm(true);
            }}
          >
            + Create New User
          </Button>
        </div>

        <Dialog
          open={showCreateForm}
          onClose={(event, reason) => {
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
              setShowCreateForm(false);
              resetForm();
            }
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
            <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
            <DialogContent dividers sx={{ pt: 2, '& .form-group': { marginBottom: 2 } }}>
              <div className="form-row">
                <div className="form-group">
                  <label>>{t('userManagement.form.name')}</label>
                  <input
                    type="text"
                    name="name"
                    className="form-input"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>>{t('userManagement.form.email')}</label>
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={!!editingUser}
                  />
                </div>
              </div>

              {!editingUser && (
                <div className="form-group">
                  <label>>{t('userManagement.form.password')}</label>
                  <input
                    type="password"
                    name="password"
                    className="form-input"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                  />
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>>{t('userManagement.form.phone')}</label>
                  <input
                    type="tel"
                    name="phone"
                    className="form-input"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>>{t('userManagement.form.role')}</label>
                  <select
                    name="role"
                    className="form-input"
                    value={formData.role}
                    onChange={handleChange}
                    required
                  >
                    <option value="manager">{t('userManagement.form.roleManager')}</option>
                    <option value="admin">{t('userManagement.form.roleAdmin')}</option>
                  </select>
                </div>
                {editingUser && (
                  <div className="form-group">
                    <label>>{t('userManagement.form.status')}</label>
                    <select
                      name="status"
                      className="form-input"
                      value={formData.status}
                      onChange={handleChange}
                      required
                    >
                      <option value="active">{t('userManagement.form.statusActive')}</option>
                      <option value="inactive">{t('userManagement.form.statusInactive')}</option>
                    </select>
                  </div>
                )}
              </div>

                {formData.role === 'manager' && (
                <>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="verified"
                        checked={formData.verified}
                        onChange={(e) => setFormData(prev => ({ ...prev, verified: e.target.checked }))}
                      />
                      <span>{t('userManagement.form.verified')}</span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label>>{t('userManagement.form.assignRestaurant')}</label>
                    <select
                      name="restaurantId"
                      className="form-input"
                      value={formData.restaurantIds.length > 0 ? formData.restaurantIds[0] : ''}
                      onChange={handleChange}
                    >
                      <option value="">{t('userManagement.form.noRestaurant')}</option>
                      {availableRestaurantsForAssignment.map(restaurant => (
                        <option key={restaurant.id} value={restaurant.id}>
                          {restaurant.name}
                        </option>
                      ))}
                    </select>
                    <span className="form-hint">
                      {editingUser
                        ? 'Each user can be assigned to one restaurant only. Restaurants already assigned to other users are hidden.'
                        : 'Each user can be assigned to one restaurant only. Restaurants already assigned are hidden.'}
                    </span>
                  </div>
                </>
              )}

            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {editingUser ? 'Update User' : 'Create User'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Verify / Unverify confirmation dialog */}
        <Dialog
          open={verifyDialogOpen}
          onClose={handleVerifyDialogCancel}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle>
            {verifyDialogUser && Boolean(verifyDialogUser.verified) ? t('userManagement.verifyDialog.unverifyTitle') : t('userManagement.verifyDialog.verifyTitle')}}
          </DialogTitle>
          <DialogContent>
            {verifyDialogUser && (
              <p style={{ margin: 0 }}>
                {Boolean(verifyDialogUser.verified)
                  ? `Unverify ${verifyDialogUser.name}? They will no longer be able to create or manage restaurants.`
                  : `Verify ${verifyDialogUser.name}? They will be able to create and manage restaurants.`}
              </p>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleVerifyDialogCancel}>>{t('common.cancel')}</Button>
            <Button onClick={handleVerifyDialogConfirm} variant="contained" color="primary">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete user confirmation dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteDialogCancel}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle>{t('userManagement.deleteDialog.title')}</DialogTitle>
          <DialogContent>
            {deleteDialogUser && (
              <p style={{ margin: 0 }}>
                Are you sure you want to delete <strong>{deleteDialogUser.name}</strong>? This cannot be undone.
              </p>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleDeleteDialogCancel}>>{t('common.cancel')}</Button>
            <Button onClick={handleDeleteDialogConfirm} variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Admin reset-password dialog */}
        <Dialog
          open={resetPasswordDialogOpen}
          onClose={handleResetPasswordCancel}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <form onSubmit={handleResetPasswordConfirm}>
            <DialogTitle>{t('userManagement.resetPasswordDialog.title')}</DialogTitle>
            <DialogContent dividers sx={{ pt: 2 }}>
              {resetPasswordUser && (
                <p style={{ marginTop: 0 }}>
                  {t('userManagement.resetPasswordDialog.message', { name: resetPasswordUser.name })}
                </p>
              )}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>{t('userManagement.resetPasswordDialog.newPasswordLabel')}</label>
                <input
                  type="password"
                  className="form-input"
                  value={resetPasswordValue}
                  onChange={(e) => {
                    setResetPasswordValue(e.target.value);
                    if (resetPasswordError) setResetPasswordError('');
                  }}
                  required
                  minLength={8}
                  autoFocus
                />
              </div>
              {resetPasswordError && (
                <p style={{ color: 'var(--error, #ef4444)', marginTop: 8, marginBottom: 0 }}>
                  {resetPasswordError}
                </p>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button type="button" onClick={handleResetPasswordCancel} disabled={resetPasswordSubmitting}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="contained" color="primary" disabled={resetPasswordSubmitting}>
                {resetPasswordSubmitting
                  ? t('userManagement.resetPasswordDialog.submitting')
                  : t('userManagement.resetPasswordDialog.confirm')}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Result message dialog (e.g. "User unverified" / "User verified") */}
        <Dialog
          open={resultDialogOpen}
          onClose={() => setResultDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle>>{t('common.notice')}</DialogTitle>
          <DialogContent>
            <p style={{ margin: 0 }}>{resultDialogMessage}</p>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setResultDialogOpen(false)} variant="contained" color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>

        <div className="users-table-container">
          <h2>{t('userManagement.allUsers')}</h2>
          {loading ? (
            <div className="loading-container">
              <div className="loading"></div>
            </div>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th>{t('userManagement.table.name')}</th>
                  <th>{t('userManagement.table.email')}</th>
                  <th>{t('userManagement.table.role')}</th>
                  <th>{t('userManagement.table.status')}</th>
                  <th>{t('userManagement.table.verified')}</th>
                  <th>{t('userManagement.table.restaurants')}</th>
                  <th>{t('userManagement.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td data-label={t('userManagement.table.name')}>{user.name}</td>
                    <td data-label={t('userManagement.table.email')}>{user.email}</td>
                    <td data-label={t('userManagement.table.role')}>
                      <span className={`role-badge ${user.role}`}>
                        {user.role}
                      </span>
                    </td>
                    <td data-label={t('userManagement.table.status')}>
                      <span className={`status-badge ${user.status}`}>
                        {user.status}
                      </span>
                    </td>
                    <td data-label={t('userManagement.table.verified')}>
                      {user.role === 'manager' ? (
                        <span className={`status-badge ${Boolean(user.verified) ? 'active' : 'inactive'}`}>
                          {Boolean(user.verified) ? 'Yes' : 'No'}
                        </span>
                      ) : (
                        <span className="status-badge">—</span>
                      )}
                    </td>
                    <td data-label={t('userManagement.table.restaurants')}>
                      {user.restaurant_names && user.restaurant_names.length > 0
                        ? user.restaurant_names[0]
                        : 'None'}
                    </td>
                    <td data-label={t('userManagement.table.actions')}>
                      <div className="action-buttons">
                        {user.role === 'manager' && (
                          <button
                            className={`btn btn-small ${Boolean(user.verified) ? 'btn-secondary' : 'btn-primary'}`}
                            onClick={() => handleToggleVerifyClick(user)}
                            title={Boolean(user.verified) ? 'Unverify (revoke restaurant creation)' : 'Verify (allow restaurant creation)'}
                          >
                            {Boolean(user.verified) ? 'Unverify' : 'Verify'}
                          </button>
                        )}
                        <button
                          className="btn btn-secondary btn-small"
                          onClick={() => handleEditUser(user)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-secondary btn-small"
                          onClick={() => handleResetPasswordClick(user)}
                          title={t('userManagement.resetPasswordDialog.actionHint')}
                        >
                          {t('userManagement.resetPasswordDialog.actionLabel')}
                        </button>
                        <button
                          className="btn btn-danger btn-small"
                          onClick={() => handleDeleteClick(user)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
    </AppShell>
  );
};

export default UserManagement;
